import ContentsToReact from '@/components/ContentsToReact/ContentsToReact';
import GenericModals from '@/components/GenericModals/GenericModals';
import Page from '@/components/Page/Page';
import PageHead from '@/components/PageHead/PageHead';
import convertPressArticleToCardProp from '@/helpers/convertPressArticleToCardProp';
import { extractContents } from '@/helpers/extractContents';
import extractPageMetaFromRouteData from '@/helpers/extractPageMetaFromRouteData';
import spliceHeaderFromContentItems from '@/helpers/spliceHeaderFromContentItems';
import useBodyClass from '@/hooks/useBodyClass/useBodyClass';
import { useInitialPageDataLayer } from '@/hooks/useInitialPageDataLayer/useInitialPageDataLayer';
import usePressArticles from '@/hooks/usePressArticles/usePressArticles';
import useShowMore from '@/hooks/useShowMore/useShowMore';
import { PageModel } from '@/models/page';
import Button from '@uikit/components/Button/Button';
import {
  ButtonAlign,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
} from '@uikit/components/Button/consts';
import CardGrid from '@uikit/components/CardGrid/CardGrid';
import { ClickableAction } from '@uikit/components/Clickable/consts';
import Container from '@uikit/components/Container/Container';
import { ContainerChildrenSpace } from '@uikit/components/Container/consts';
import EnercityFooter from '@uikit/components/EnercityFooter/EnercityFooter';
import EventCarousel from '@uikit/components/EventCarousel/EventCarousel';
import Headline from '@uikit/components/Headline/Headline';
import {
  HeadlineAlign,
  HeadlineColor,
  HeadlineLevel,
} from '@uikit/components/Headline/consts';
import PressContactList from '@uikit/components/PressContactList/PressContactList';
import PressGrid from '@uikit/components/PressGrid/PressGrid';
import PressLayout from '@uikit/components/PressLayout/PressLayout';
import Section from '@uikit/components/Section/Section';
import {
  SectionBackgroundColor,
  SectionVariant,
} from '@uikit/components/Section/consts';
import Spacer from '@uikit/components/Spacer/Spacer';
import Stack from '@uikit/components/Stack/Stack';
import PressOnCallButton from '@uikit/composites/PressOnCallButton/PressOnCallButton';
import cmsComponents from './PressPage.cmsComponents';
import type { PressPageProps } from './interface';

const PressPage = (routeData: PageModel<PressPageProps>) => {
  const meta = extractPageMetaFromRouteData(routeData);
  const {
    contents,
    modals,
    props: {
      pressCardArticles = [],
      pressEventArticles = [],
      pressContacts = [],
      pressType,
    },
  } = routeData;

  const { header } = spliceHeaderFromContentItems(contents);
  const {
    extractedContents: [additionalContents],
  } = extractContents(['additional-contents'], contents);
  const headerProps = header?.props;

  useBodyClass(['press']);

  const { data } = usePressArticles();

  const filteredData =
    data
      ?.filter((item) => item.pressType === pressType)
      .filter((item) => !pressCardArticles.some(({ id }) => id === item.id))
      .filter((item) => item.pressType !== 'constructions') ?? [];

  const {
    currentPageItems: currentPageArticles,
    next,
    canGoNext,
  } = useShowMore(filteredData, { itemsPerPage: 3, initialPage: 0 });

  const pressGridItems = currentPageArticles.map(convertPressArticleToCardProp);

  useInitialPageDataLayer({ resourceType: 'frontpage' });

  return (
    <>
      <PageHead {...meta} />
      <PressLayout>
        <Page>
          {header && (
            <ContentsToReact
              contents={[
                {
                  ...header,
                  props: { ...headerProps, subHeadlineAsH1: true },
                },
              ]}
              cmsComponents={cmsComponents}
            />
          )}
          <Section resetTopSpace>
            <Container spaceBetweenChildren={ContainerChildrenSpace.None}>
              <Spacer defaultSize={1} md={2} />
              <CardGrid items={pressCardArticles} />
              <PressGrid items={pressGridItems} />
              {canGoNext && (
                <>
                  <Spacer defaultSize={1} sm={2} />
                  <Button
                    actionType={ClickableAction.Custom}
                    color={ButtonColor.Primary}
                    onClick={next}
                    size={ButtonSize.Medium}
                    width={ButtonWidth.Auto}
                    align={ButtonAlign.Center}
                  >
                    Mehr anzeigen
                  </Button>
                </>
              )}
            </Container>
          </Section>

          {pressEventArticles && pressEventArticles.length !== 0 && (
            <Section bgColor={SectionBackgroundColor.Violet} hasPadding={false}>
              <Container
                spaceBottom={5}
                spaceTop={5}
                spaceBetweenChildren={ContainerChildrenSpace.None}
              >
                <Headline
                  headlineColor={HeadlineColor.Gradient}
                  level={HeadlineLevel.Two}
                  subText="Die wichtigsten Medienevents. Direkt für Ihren Kalender."
                  tag="h2"
                  align={HeadlineAlign.Center}
                >
                  Events &amp; Termine
                </Headline>

                <Spacer defaultSize={1} sm={3} />

                <EventCarousel events={pressEventArticles} />
              </Container>
            </Section>
          )}

          <Section variant={SectionVariant.LargePaddingBottom}>
            <Container>
              <Headline
                headlineColor={HeadlineColor.Gradient}
                level={2}
                isCustomWidth={true}
                subText={`
                  Wir sind gerne für Sie da. Bitte haben Sie jedoch Verständnis, dass wir an dieser Stelle ausschließlich Anfragen von
                  Journalisten und weiteren Medienschaffenden beantworten können.
                `}
                tag="h2"
                align={HeadlineAlign.Left}
              >
                Pressekontakt
              </Headline>
              <PressContactList items={pressContacts} />
              <Stack gap={1}>
                <Headline
                  headlineColor={HeadlineColor.Dark}
                  level={4}
                  align={HeadlineAlign.Left}
                >
                  Unser Service für Journalist:innen: die
                  Presse-Rufbereitschaft.
                </Headline>

                <PressOnCallButton />
              </Stack>
            </Container>
          </Section>

          {additionalContents && (
            <ContentsToReact
              contents={additionalContents}
              cmsComponents={cmsComponents}
            />
          )}
        </Page>
        <EnercityFooter />
      </PressLayout>
      <GenericModals modals={modals} cmsComponents={cmsComponents} />
    </>
  );
};

export default PressPage;
